import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Input,
  FormControl,
  FormLabel,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer,
  Image,
  Box,
  useToast,
  VStack,
  Flex
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { log } from '../../utils/logger'
import { createMultipartServicesApi, createCategoriesActivitiesApi } from './../../utils/api-interface'
import { UpdateCategoryRequest, Category, CategoryListResponse, UpdateCategoryResponse, LocalizedName, DashboardProfile } from '../../services/network'
import { DeleteConfirmationDialog } from '../DeleteConfirmationDialog/DeleteConfirmationDialog'
import axios from 'axios'
import { languages, ICategoryName, ICollapsedLangs } from '../../utils/constants'
import { handleCategoryNameChange } from '../../utils/modalActivitiesCategoriesUtils'
import { getErrorMessage } from '../../utils/utils'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/store'

interface ICreateCategoryModalProps {
  isOpen: boolean
  onClose: () => void
  category: Category
  setIsModifyCategoryModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  categories: Category[]
  setCategories: React.Dispatch<React.SetStateAction<CategoryListResponse | undefined>>
}

function ModifyCategoryModal({ isOpen, onClose, category, setIsModifyCategoryModalOpen, setCategories }: ICreateCategoryModalProps): JSX.Element {
  const { t } = useTranslation()
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined)
  const [selectedImageURL, setSelectedImageURL] = useState('')
  const [confirmButtonDisabled, setConfirmButtonDisabled] = useState(true)
  const [updateCategoryRequest, setUpdateCategoryRequest] = useState<UpdateCategoryRequest>()
  const toast = useToast()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [collapsedLangs, setCollapsedLangs] = useState<ICollapsedLangs>(languages.reduce((acc, lang) => ({ ...acc, [lang.code]: false }), {}))
  const [categoryNames, setCategoryNames] = useState<ICategoryName>(languages.reduce((acc, lang) => ({ ...acc, [lang.code]: '' }), {}))
  const [showTranslateMenu, setShowTranslateMenu] = useState(false)
  const [open, setOpen] = useState(isOpen)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const dashboardUser: DashboardProfile | null = useSelector((state: RootState) => state.dashboardUser.dashboardUser)
  const canDelete = useMemo(() => {
    if (!dashboardUser) {
      return false
    }

    if (!dashboardUser.role || !dashboardUser.role.title) {
      return false
    }

    return dashboardUser.role.title === process.env.REACT_APP_ROLE_ADMINISTRATOR
  }, [dashboardUser])

  const handleBoxClick = (): void => {
    if (inputRef.current) {
      inputRef.current.click()
    }
  }

  const handleCloseDialog = (): void => {
    setIsDeleteDialogOpen(false)
    setShowTranslateMenu(false)
    setCategoryNames({})
  }

  const handleDeleteButton = useCallback((): void => {
    if (canDelete) {
      setIsDeleteDialogOpen(true)
    }
  }, [canDelete])

  const deleteCategory = useCallback(async (): Promise<void> => {
    if (!canDelete) {
      return
    }
    const categoriesActivitiesApi = createCategoriesActivitiesApi()

    try {
      const response = await categoriesActivitiesApi.deleteCategory(category.id)
      log.debug(response.data)
      setCategories((prevState) => {
        if (!prevState) return prevState
        const updatedCategories = prevState.categories.filter((categoryState) => categoryState.id !== category.id)
        return { ...prevState, categories: updatedCategories }
      })

      setIsModifyCategoryModalOpen(false)
      toast({
        title: t('category_deleted_toast'),
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error) {
      log.error('Error deleting category:', error)
      toast({
        title: t('error_delete_category'),
        description: getErrorMessage(error),
        status: 'error',
        duration: 2000,
        isClosable: true
      })
    } finally {
      setIsDeleteDialogOpen(false)
    }
  }, [canDelete, category.id, setCategories, setIsModifyCategoryModalOpen, t, toast])

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e): void => {
      const image = new window.Image()
      image.src = e.target?.result as string

      image.onload = (): void => {
        if (image.width === 36 && image.height === 36) {
          setSelectedImage(file)
          setSelectedImageURL(image.src)
        } else {
          toast({
            title: 'Invalid Image Size',
            description: 'Please upload an image of size 36x36 pixels.',
            status: 'error',
            duration: 5000,
            isClosable: true
          })
          inputRef.current!.value = ''
        }
      }
    }
    reader.readAsDataURL(file)
  }

  const modifyCategory = async (): Promise<void> => {
    if (updateCategoryRequest) {
      try {
        const categoriesActivitiesApi = createMultipartServicesApi()
        const response = await categoriesActivitiesApi.updateCategory(category.id, updateCategoryRequest.icon, updateCategoryRequest.description)
        log.debug('categoryUpdate', response.data)
        const updatedCategory: UpdateCategoryResponse = response.data
        setCategories((prevState) => {
          if (!prevState) return prevState
          const updatedCategories = prevState.categories.map((cat) =>
            cat.id === updatedCategory.id
              ? {
                  ...cat,
                  iconUrl: updatedCategory.iconUrl,
                  description: updatedCategory.description,
                  activities: updatedCategory.activities || []
                }
              : cat
          )
          return { ...prevState, categories: updatedCategories }
        })
        toast({
          title: t('category_updated_toast'),
          status: 'success',
          duration: 2000,
          isClosable: true
        })
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response) {
            log.error('Error updating category:', error.response.data)
            toast({
              title: `${t('retry_modal_title')} ${error.response.data.message} (${error.response.data.statusCode})`,
              status: 'error',
              duration: 2000,
              isClosable: true
            })
          } else {
            log.error('No response from the server:', error.message)
          }
        }
      }
    }
  }

  const toggleCollapseLang = (langCode: string): void => {
    setCollapsedLangs((prevState) => ({
      ...prevState,
      [langCode]: !prevState[langCode]
    }))
  }

  const handleCategoryNameChangeCallback = useCallback(
    (langCode: string, value: string) => {
      handleCategoryNameChange(langCode, value, showTranslateMenu, categoryNames, setCategoryNames)
    },
    [categoryNames, showTranslateMenu]
  )

  const handleUpload = (): void => {
    modifyCategory()
    onClose()
  }

  const handleClose = (): void => {
    onClose()
    setCategoryNames({})
    setSelectedImageURL('')
    setSelectedImage(undefined)
    setUpdateCategoryRequest(undefined)
    setShowTranslateMenu(false)
  }

  useEffect(() => {
    if (selectedImageURL && categoryNames && categoryNames.it) {
      setConfirmButtonDisabled(false)
      setUpdateCategoryRequest({
        icon: selectedImage,
        description: categoryNames as unknown as LocalizedName
      })
    } else {
      setConfirmButtonDisabled(true)
    }
  }, [selectedImageURL, categoryNames, selectedImage])

  useEffect(() => {
    setOpen(isOpen)
  }, [isOpen])

  useEffect(() => {
    eva.replace()
    if (category) {
      setCategoryNames(category.description as unknown as ICategoryName)
      setSelectedImageURL(category.iconUrl)
    }
  }, [open, category])

  useEffect(() => {
    eva.replace()
  }, [open, showTranslateMenu])

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} isCentered={true}>
        <ModalOverlay />
        <ModalContent marginBottom={'0px'} borderRadius={'26px 26px 0px 0px'}>
          {showTranslateMenu ? (
            <Flex alignItems='left' direction={'column'}>
              <ModalHeader>
                <Flex align='center' justify='space-between' width='100%'>
                  <Text color={'black'} fontWeight='700' fontSize='24px' whiteSpace='nowrap'>
                    {t('translate_every_lang')}
                  </Text>
                  <Button onClick={(): void => setShowTranslateMenu(false)}>
                    <Flex key={'translate'} align={'center'}>
                      <i data-eva={'globe-outline'} data-eva-fill={'#7C8A97'}></i>
                      <Text color={'greyCold500'} fontWeight='600' fontSize='12px' lineHeight='16.8px' whiteSpace='nowrap'>
                        {t('translate')}
                      </Text>
                    </Flex>
                  </Button>
                </Flex>
              </ModalHeader>
              <ModalBody overflowY={'auto'} maxH={'300px'}>
                {languages.map((lang) => (
                  <Flex
                    key={lang.code}
                    alignItems='left'
                    direction={'column'}
                    bg={'rgba(143, 155, 179, 0.16)'}
                    borderRadius={'12px'}
                    padding={'16px'}
                    gap={'14px'}
                    marginBottom={'20px'}>
                    <Flex justify='space-between'>
                      <Text color={'black'} fontWeight='600' fontSize='22.4px' whiteSpace='nowrap'>
                        {t(lang.name)}
                      </Text>
                      {collapsedLangs[lang.code] ? (
                        <Button onClick={(): void => toggleCollapseLang(lang.code)}>
                          <i data-eva='minus-circle-outline' data-eva-fill={'#DD8B25'}></i>
                        </Button>
                      ) : (
                        <Button onClick={(): void => toggleCollapseLang(lang.code)}>
                          <i data-eva='plus-circle-outline' data-eva-fill={'#DD8B25'}></i>
                        </Button>
                      )}
                    </Flex>
                    {collapsedLangs[lang.code] && (
                      <div>
                        <FormControl id={`${lang.code}-name`}>
                          <FormLabel variant={'primary'}>{t('name_category')}</FormLabel>
                          <Input
                            bg='white'
                            value={categoryNames[lang.code]}
                            onChange={(e): void => handleCategoryNameChangeCallback(lang.code, e.target.value)}
                          />
                        </FormControl>
                      </div>
                    )}
                  </Flex>
                ))}
              </ModalBody>
              <ModalFooter justifyContent='center'>
                <Button variant='outline' width={'50%'} height='50px' color={'primary.400'} borderColor={'primary.400'} onClick={handleClose} margin={'5px'}>
                  {t('cancel')}
                </Button>
                <Button variant={'primary'} width={'50%'} height='50px' onClick={(): void => setShowTranslateMenu(false)} margin={'5px'}>
                  {t('confirm')}
                </Button>
              </ModalFooter>
            </Flex>
          ) : (
            <Flex alignItems='left' direction={'column'}>
              <ModalHeader fontSize='24px' fontWeight='700'>
                <Flex align='center' justify='space-between' width='100%'>
                  <Text color={'black'} fontWeight='700' fontSize='24px' whiteSpace='nowrap'>
                    {t('modify_category')}
                  </Text>
                  <Button onClick={(): void => setShowTranslateMenu(true)}>
                    <Flex key={'translate'} align={'center'}>
                      <i data-eva={'globe-outline'} data-eva-fill={'#7C8A97'}></i>
                      <Text color={'greyCold500'} fontWeight='600' fontSize='12px' lineHeight='16.8px' whiteSpace='nowrap'>
                        Traduci
                      </Text>
                    </Flex>
                  </Button>
                </Flex>
              </ModalHeader>

              <ModalBody overflowY={'auto'} maxH={'300px'}>
                <HStack width={'full'} marginBottom={'4px'} key={'firstHstack'}>
                  <FormControl id='icon'>
                    <FormLabel variant={'primary'}>{t('icon')}</FormLabel>
                    <Box
                      key={'imagecontainer'}
                      alignContent={'center'}
                      width='48px'
                      height='48px'
                      borderRadius='full'
                      border='2px'
                      borderColor='greyCold100'
                      background={'greyCold100'}
                      minWidth='fit-content'
                      onClick={handleBoxClick}>
                      {selectedImageURL ? (
                        <Image key={'imageuploaded'} src={selectedImageURL} boxSize='100%' objectFit='cover' borderRadius='full' />
                      ) : (
                        <VStack key={'logoimage'}>
                          <i data-eva={'upload-outline'} data-eva-fill={'#353F48'}></i>
                        </VStack>
                      )}
                      <Input ref={inputRef} type='file' accept='image/*' onChange={handleImageUpload} display={'none'} />
                    </Box>
                  </FormControl>
                  <Spacer></Spacer>
                  <i id='test' data-eva={'info'} data-eva-fill={'#B0BBC4'}></i>
                  <Text fontSize='12px' color='gray.500' minWidth={'fit-content'}>
                    {t('icon_info')}
                  </Text>
                </HStack>

                <FormControl id='name'>
                  <FormLabel variant={'primary'}>{t('first_name')}</FormLabel>
                  <Input bg='white' value={categoryNames['it']} onChange={(e): void => handleCategoryNameChangeCallback('it', e.target.value)} />
                </FormControl>

                {canDelete && (
                  <Button
                    marginTop={'20px'}
                    padding={'0px'}
                    key='deleteaccount'
                    onClick={(): void => {
                      handleDeleteButton()
                    }}>
                    <i data-eva={'trash-2'} data-eva-fill={'#353F48'}></i>
                    <Text
                      marginLeft={'5px'}
                      fontWeight='600'
                      fontSize='16px'
                      lineHeight='22.4px'
                      color={'greyCold200'}
                      _hover={{
                        color: 'black'
                      }}>
                      {t('delete_permanently')}
                    </Text>
                  </Button>
                )}
              </ModalBody>

              <ModalFooter justifyContent='center'>
                <Button variant='outline' width={'50%'} height='50px' color={'primary.400'} borderColor={'primary.400'} onClick={handleClose} margin={'5px'}>
                  {t('cancel')}
                </Button>
                <Button variant={'primary'} width={'50%'} height='50px' isDisabled={confirmButtonDisabled} onClick={handleUpload} margin={'5px'}>
                  {t('save')}
                </Button>
              </ModalFooter>
            </Flex>
          )}
        </ModalContent>
      </Modal>
      {canDelete && <DeleteConfirmationDialog isOpen={isDeleteDialogOpen} onClose={handleCloseDialog} onConfirm={deleteCategory} type='category' />}
    </>
  )
}

export { ModifyCategoryModal }
